import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { calculatePrice, isValidQuantity } from '../utils/orderFormUtils';

/**
 * Custom hook for handling category from URL parameters
 * Stops processing URL params if user manually selected category
 */
export const useOrderFormCategoryParams = (
  setCategory,           // Function to set category (same as CategorySelector uses)
  category,             // Current category value
  hasManualSelection    // Flag to track if user manually selected category
) => {
  const location = useLocation();

  useEffect(() => {
    // Don't process URL params if user has manually selected a category
    if (hasManualSelection) {
      console.log('Skipping URL category processing - manual selection detected');
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');

    console.log('Category URL Parameters:', location.search);
    console.log('Category param:', categoryParam, 'Current category:', category);

    // Only set category if provided and different from current
    if (categoryParam && category !== categoryParam) {
      console.log('Setting category from URL:', categoryParam);
      setCategory(categoryParam);
    }
  }, [location.search, category, setCategory, hasManualSelection]);
};

/**
 * Custom hook for handling service selection from URL parameters
 * Only runs after category is set and services are loaded
 * Stops processing if user made manual selection
 */
export const useOrderFormServiceParams = (
  handleServiceSelect,   // Function to select service (same as ServiceSelector uses)
  serviceList,           // The loaded service list
  category,             // Current category value (must be set first)
  hasManualSelection    // Flag to track if user manually selected anything
) => {
  const location = useLocation();

  useEffect(() => {
    // Don't process URL params if user has manually selected anything
    if (hasManualSelection) {
      console.log('Skipping URL service processing - manual selection detected');
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const productIdParam = urlParams.get('productId');

    console.log('Service URL Parameters:', location.search);
    console.log('ProductId param:', productIdParam, 'Category:', category, 'Service list length:', serviceList.length);

    // Only try to select service if:
    // - We have a productId from URL
    // - We have a category set (either from URL or manually)
    // - We have services loaded for that category
    if (productIdParam && category && serviceList.length > 0) {
      const targetService = serviceList.find(service => service.id === Number(productIdParam));
      if (targetService) {
        console.log('Found matching service:', targetService);
        handleServiceSelect(targetService.name, targetService.id, targetService);
      } else {
        console.log('No matching service found for productId:', productIdParam, 'in category:', category);
      }
    }
  }, [serviceList, category, handleServiceSelect, location.search, hasManualSelection]);
};

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useOrderFormCategoryParams and useOrderFormServiceParams instead
 */
export const useOrderFormURLParams = (
  setCategory,           // Function to set category (same as CategorySelector uses)
  handleServiceSelect,   // Function to select service (same as ServiceSelector uses)
  serviceList,           // The loaded service list
  category,              // Current category value
  setServiceId,          // Only set this after finding the service
  setProduct_id          // Only set this after finding the service
) => {
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const productIdParam = urlParams.get('productId');

    console.log('URL Parameters:', location.search);
    console.log('Parsed Params - category:', categoryParam, 'productId:', productIdParam);
    console.log('Current category:', category, 'Service list length:', serviceList.length);

    // 1. Set category if provided (same as user clicking category)
    if (categoryParam && category !== categoryParam) {
      console.log('Setting category from URL:', categoryParam);
      setCategory(categoryParam);
    }

    // 2. Only try to select service if:
    //    - We have a productId from URL
    //    - We have a category set (either from URL or previously)
    //    - We have services loaded for that category
    if (productIdParam && category && serviceList.length > 0) {
      const targetService = serviceList.find(service => service.id === Number(productIdParam));
      if (targetService) {
        console.log('Found matching service:', targetService);
        handleServiceSelect(targetService.name, targetService.id, targetService);
      } else {
        console.log('No matching service found for productId:', productIdParam, 'in category:', category);
      }
    }
  }, [location.search, category, serviceList, setCategory, handleServiceSelect]);
};

/**
 * Custom hook for price calculation
 */
export const useOrderFormPriceCalculation = (quantity, servicePrice, setprice) => {
  useEffect(() => {
    setprice(calculatePrice(quantity, servicePrice));
  }, [quantity, servicePrice]);
};

/**
 * Custom hook for video data visibility
 */
export const useOrderFormVideoVisibility = (videoData, setIsButtonVisible) => {
  useEffect(() => {
    if (videoData) {
      setIsButtonVisible(true);
    }
  }, [videoData]);
};

/**
 * Custom hook for auto-selecting service from URL params
 */
export const useOrderFormAutoSelect = (serviceId, serviceList, setProduct_id, setService, setServicePrice, setService_Type, setSelectedServiceId) => {
  useEffect(() => {
    if (serviceId && serviceList.length > 0) {
      const selectedService = serviceList.find(service => service.service_id === serviceId);

      if (selectedService) {
        // Only update if the value is different to prevent unnecessary re-renders
        setProduct_id(prevProductId => prevProductId !== selectedService.id ? Number(selectedService.id) : prevProductId);
        setService(prevService => prevService !== selectedService.name ? selectedService.name : prevService);
        setServicePrice(prevPrice => prevPrice !== selectedService.price ? Number(selectedService.price) : prevPrice);
        setService_Type(prevType => prevType !== selectedService.type ? selectedService.type : prevType);
        setSelectedServiceId(prevSelectedId => prevSelectedId !== selectedService.id ? Number(selectedService.id) : prevSelectedId);
      }
    }
  }, []);
};
